import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/client';
import { useDebounced } from '@/components/parameters/useDebounced';
import { OwnerPicker } from '@/components/OwnerPicker';
import { HeadOfficePicker } from '@/components/HeadOfficePicker';
import { ParametersEditor } from '@/components/ParametersEditor';
import type {
  DirectoryUser,
  ReportPackApplyItemResult,
  ReportPackApplyPreview,
  ReportPackApplyResult,
  ReportPackDetail,
  ReportPackItemOverride,
  ReportPackPreviewItem,
  ReportParameterValue
} from '@/types';

/** The customer this pack is being applied to, exactly as Profiles spells it. */
type ChosenCustomer = { headOfficeId: number; name: string };

/**
 * Every item's per-customer adjustments, by item id, as the wizard holds them while they are being
 * typed. Sent to the server as a list — see {@link ReportPackItemOverride}.
 */
type OverrideMap = Record<string, ReportParameterValue[]>;

function toOverrides(map: OverrideMap): ReportPackItemOverride[] {
  return Object.entries(map)
    .filter(([, parameters]) => parameters.length > 0)
    .map(([itemId, parameters]) => ({ itemId, parameters }));
}

export function ReportPackApply() {
  const api = useApi();
  const navigate = useNavigate();
  const { packId } = useParams<{ packId: string }>();

  const [customer, setCustomer] = useState<ChosenCustomer | null>(null);
  const [owner, setOwner] = useState<DirectoryUser | null>(null);
  const [delegating, setDelegating] = useState(false);
  const [result, setResult] = useState<ReportPackApplyResult | null>(null);

  /**
   * What this customer gets that the pack does not say. Cleared with the customer: an adjustment is
   * a statement about one customer, and carrying it to the next one would silently give them
   * somebody else's choices.
   */
  const [overrides, setOverrides] = useState<OverrideMap>({});

  function chooseCustomer(next: ChosenCustomer | null) {
    setCustomer(next);
    setOverrides({});
  }

  const { data: pack } = useQuery({
    queryKey: ['pack', packId],
    queryFn: () => api.getPack(packId!),
    enabled: Boolean(packId),
    retry: false
  });

  // Already cached under this key by the app shell, so reading the caller's name here costs no
  // round trip. It is only ever displayed — the API defaults the portal owner to the caller itself.
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me, staleTime: Infinity });

  /**
   * What applying would do. A query rather than a mutation because it writes nothing and is safe to
   * re-run — which is exactly what makes it re-askable every time the customer changes, and
   * cacheable so stepping back to the customer and forward again does not re-ask MANDW01.
   */
  /**
   * The overrides as the preview key sees them, debounced.
   *
   * Every keystroke must eventually reach the preview — it is what decides whether an item is
   * refused for a missing required parameter, and a preview describing values the admin has since
   * changed would be describing something other than what the button creates. But a preview costs
   * MANDW01 a parameter resolve per item, so it waits for the typing to stop rather than chasing it.
   */
  const overridesKey = useDebounced(JSON.stringify(toOverrides(overrides)), 700);

  const preview = useQuery({
    queryKey: ['pack-preview', packId, customer?.headOfficeId, overridesKey],
    queryFn: () => api.previewPack({
      packId: packId!,
      headOfficeId: customer!.headOfficeId,
      headOfficeName: customer!.name,
      overrides: JSON.parse(overridesKey) as ReportPackItemOverride[]
    }),
    enabled: Boolean(packId && customer),
    // Keeps the previous answer on screen while a re-preview runs, so adjusting a parameter does
    // not blank the list of items underneath the field being edited.
    placeholderData: previous => previous,
    retry: false
  });

  const apply = useMutation({
    mutationFn: (itemIds: string[]) => api.applyPack({
      packId: packId!,
      headOfficeId: customer!.headOfficeId,
      headOfficeName: customer!.name,
      itemIds,
      portalOwnerObjectId: owner?.objectId ?? null,
      portalOwnerDisplayName: owner?.displayName ?? null,
      // Not the debounced copy. The button can be pressed before the preview has caught up, and
      // what the admin typed is what they meant — the server re-runs the preview against exactly
      // this set before it creates anything, so the authority is the same either way.
      overrides: toOverrides(overrides)
    }),
    onSuccess: setResult
  });

  if (!packId) return <div className="error-banner">No pack was named in the address.</div>;

  if (result) {
    return (
      <ApplyResults
        result={result}
        onApplyAnother={() => {
          setResult(null);
          chooseCustomer(null);
          apply.reset();
        }}
      />
    );
  }

  const step: 1 | 2 = customer ? 2 : 1;
  const items = preview.data?.items ?? [];
  // Everything not blocked. Blocked items are the ones the API has already said it will refuse, so
  // sending them back would ask it to refuse them a second time; the ones that already exist stay
  // in, because the double-apply guard on the server is the authority on those, not this screen.
  const creatable = items.filter(i => i.outcome !== 'Blocked');
  const willCreate = items.filter(i => i.outcome === 'WillCreate').length;

  /**
   * True while the preview on screen is older than the adjustments underneath it.
   *
   * The Create button is disabled until it catches up, and that is not cosmetic: the item ids sent
   * are the ones the preview says are creatable, so pressing Create against a stale preview would
   * silently leave out the very item the admin has just fixed.
   */
  const previewStale = preview.isFetching
    || overridesKey !== JSON.stringify(toOverrides(overrides));

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Apply {pack ? `“${pack.name}”` : 'report pack'}</h1>
          <div className="sub">
            Nothing is created until you confirm. This preview performs no writes and is safe to
            re-run.
          </div>
        </div>
      </div>

      <Stepper step={step} />

      <div className="card" style={{ marginBottom: 18 }}>
        <div className="card-header">
          <h3>1. Customer</h3>
          {customer && (
            <button className="btn-ghost btn-sm" onClick={() => chooseCustomer(null)}>Change</button>
          )}
        </div>
        <div className="card-body">
          {customer ? (
            <>
              <div style={{ fontSize: '.9rem', color: 'var(--kt-grey-700)' }}>
                Head office <strong>{customer.headOfficeId} — {customer.name}</strong>
              </div>
              <div style={{ marginTop: 10, fontSize: '.88rem', color: 'var(--kt-grey-700)' }}>
                Portal owner: <strong>{owner?.displayName ?? me?.displayName ?? 'you'}</strong>
                {!owner && me && ' (you)'}
                {' '}
                <button className="btn-ghost btn-sm" onClick={() => setDelegating(d => !d)}>
                  {delegating ? 'Cancel' : 'Delegate'}
                </button>
              </div>
              {delegating && (
                <div style={{ marginTop: 10, maxWidth: 520 }}>
                  <OwnerPicker value={owner} onChange={setOwner} />
                  <div className="field-hint">
                    Who manages these from the portal. On the report server they are owned by
                    svc_reportportal either way — that is a separate record and this does not move it.
                  </div>
                </div>
              )}
            </>
          ) : (
            <HeadOfficePicker onChoose={chooseCustomer} />
          )}
        </div>
      </div>

      {customer && (
        <div className="card" style={{ marginBottom: 18 }}>
          <div className="card-header">
            <h3>2. What will be created</h3>
            {preview.data && <PreviewSummaryPill preview={preview.data} />}
          </div>

          {preview.isFetching && !preview.data ? (
            <div className="card-body"><div className="placeholder">Working out what this would do...</div></div>
          ) : preview.error ? (
            <div className="card-body">
              <div className="error-banner">
                The preview could not be produced, so nothing can be created yet:{' '}
                {(preview.error as Error).message.replace(/^API \d+: /, '')}
              </div>
            </div>
          ) : preview.data ? (
            <>
              {!preview.data.headOfficeConfirmed && (
                <div className="card-body" style={{ paddingBottom: 0 }}>
                  {/* An unanswerable question and a negative answer are not the same fact. This
                      says the directory could not be reached — it does not say the customer is
                      wrong, because nothing here knows that. */}
                  <div className="notice notice-warn" style={{ marginBottom: 0 }}>
                    <strong>This head office could not be confirmed against the customer directory.</strong>
                    <div>
                      The directory could not be reached, so the id and name below are the ones you
                      chose rather than ones that have been checked. Everything can still be created;
                      the pickup folder will be named from what is shown here.
                    </div>
                  </div>
                </div>
              )}

              {/* Keyed on the customer so every row — and the local state inside its parameter
                  editor — is rebuilt when the customer changes. Without it, adjustments made for
                  one customer would still be on screen for the next. */}
              <div key={preview.data.headOfficeId}>
                {preview.data.items.map(item => (
                  <PreviewRow
                    key={item.itemId}
                    item={item}
                    // The pack's own definition of this item, for its report path — the preview
                    // carries what an item WILL DO, not what it is, and the parameter editor needs
                    // the report to read definitions and resolve lists from.
                    packItem={pack?.items.find(i => i.id === item.itemId)}
                    overrides={overrides[item.itemId] ?? null}
                    onOverride={next => setOverrides(current => ({ ...current, [item.itemId]: next }))}
                    onClearOverride={() => setOverrides(current => {
                      const { [item.itemId]: _dropped, ...rest } = current;
                      return rest;
                    })}
                  />
                ))}
              </div>

              <div className="card-body" style={{
                borderTop: '1px solid var(--kt-grey-200)',
                background: 'var(--kt-grey-50)'
              }}>
                <div style={{ fontSize: '.88rem', color: 'var(--kt-grey-700)' }}>
                  Files will land in pickup under <code>{preview.data.pickupClientFolder}</code>
                </div>
                <div style={{ fontSize: '.82rem', color: 'var(--kt-grey-500)', marginTop: 6 }}>
                  Every item runs on the pack's one schedule, staggered by its position in the pack,
                  and this customer is shifted a further{' '}
                  {preview.data.scheduleSpreadMinutes} minute
                  {preview.data.scheduleSpreadMinutes === 1 ? '' : 's'} on top, so a pack applied to
                  many customers does not fire all of them at once. Both shifts are derived — the
                  first from the item's position, the second from the head office id — so re-applying
                  puts this customer back in the same slot.
                </div>
              </div>
            </>
          ) : null}
        </div>
      )}

      {apply.error && (
        <div className="error-banner">{(apply.error as Error).message.replace(/^API \d+: /, '')}</div>
      )}

      {/* Cancel stays available even when the preview failed — otherwise a failed preview leaves
          the wizard with no way out but the back button. */}
      {customer && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn-secondary" onClick={() => navigate(`/admin/packs/${packId}`)}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={apply.isPending || !preview.data || willCreate === 0 || previewStale}
            onClick={() => apply.mutate(creatable.map(i => i.itemId))}
            title={previewStale
              ? 'Working out what your adjustments change'
              : preview.data && willCreate === 0
                ? (creatable.length > 0
                  ? 'Every item on this pack already exists for this customer'
                  : 'Nothing on this pack can be created for this customer')
                : undefined}
          >
            {apply.isPending
              ? 'Creating...'
              : previewStale
                ? 'Checking...'
                : `Create ${willCreate} subscription${willCreate === 1 ? '' : 's'}`}
          </button>
        </div>
      )}
    </>
  );
}

function Stepper({ step }: { step: 1 | 2 }) {
  const steps = ['Customer', 'Preview', 'Create'];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
      {steps.map((label, index) => {
        const position = index + 1;
        const done = position < step;
        const current = position === step;
        return (
          <span key={label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {index > 0 && <span style={{ color: 'var(--kt-grey-300)' }}>&rsaquo;</span>}
            <span style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: '.86rem',
              fontWeight: 600,
              color: current ? 'var(--kt-green-dark)' : done ? 'var(--kt-green)' : 'var(--kt-grey-400)'
            }}>
              <span
                aria-hidden="true"
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '.78rem',
                  background: current ? 'var(--kt-green)' : done ? 'var(--kt-green-bg)' : 'var(--kt-grey-200)',
                  color: current ? '#fff' : done ? 'var(--kt-green)' : 'var(--kt-grey-500)'
                }}>
                {done ? '✓' : position}
              </span>
              {label}
            </span>
          </span>
        );
      })}
    </div>
  );
}

/** "3 will be created · 1 will be skipped" — counted from the rows, never from a separate number. */
function PreviewSummaryPill({ preview }: { preview: ReportPackApplyPreview }) {
  const blocked = preview.items.filter(i => i.outcome === 'Blocked').length;
  const existing = preview.items.filter(i => i.outcome === 'AlreadyExists').length;
  const skipped = blocked + existing;

  if (skipped === 0) {
    return <span className="pill pill-success">{preview.items.length} will be created</span>;
  }
  return (
    <span className="pill pill-pending">
      {skipped} of {preview.items.length} will be skipped
    </span>
  );
}

/**
 * One item of the preview.
 *
 * The reason a row is skipped is rendered in the row, in full, as its own warning — never as a
 * tooltip or a truncated line. An item being skipped is the single most important thing on this
 * screen: it is the difference between a customer getting their whole suite and getting most of it
 * and nobody noticing for six weeks.
 */
function PreviewRow({ item, packItem, overrides, onOverride, onClearOverride }: {
  item: ReportPackPreviewItem;
  packItem?: ReportPackDetail['items'][number];
  overrides: ReportParameterValue[] | null;
  onOverride: (values: ReportParameterValue[]) => void;
  onClearOverride: () => void;
}) {
  const skipped = item.outcome !== 'WillCreate';
  return (
    <div style={{
      display: 'flex',
      gap: 16,
      padding: '14px 20px',
      borderBottom: '1px solid var(--kt-grey-100)',
      alignItems: 'flex-start'
    }}>
      <div
        aria-hidden="true"
        style={{
          width: 20,
          flexShrink: 0,
          textAlign: 'center',
          lineHeight: 1.4,
          color: skipped ? 'var(--kt-orange)' : 'var(--kt-green)'
        }}>
        {skipped ? '⚠' : '✓'}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontWeight: 600,
          fontSize: '.94rem',
          color: skipped ? 'var(--kt-grey-500)' : 'var(--kt-green-dark)',
          textDecoration: item.outcome === 'Blocked' ? 'line-through' : undefined
        }}>
          {item.title}
        </div>
        <div style={{ fontSize: '.82rem', color: 'var(--kt-grey-500)', marginTop: 3 }}>
          {item.parametersDescription}
        </div>

        {item.outcome === 'Blocked' && (
          <div className="notice notice-warn" style={{ margin: '8px 0 0' }}>
            <strong>Will be skipped — every other item is still created.</strong>
            <div>{item.reason ?? 'No reason was given, which is itself worth raising with BI.'}</div>
          </div>
        )}

        {item.outcome === 'AlreadyExists' && (
          <div className="notice" style={{ border: '1px solid var(--kt-grey-200)', margin: '8px 0 0' }}>
            <strong>Already exists for this customer, so it will not be created again.</strong>
            <div>
              {item.reason ?? 'An earlier apply created this one.'}
              {item.existingSubscriptionId && (
                <>
                  {' '}
                  <Link to={`/subscriptions/${item.existingSubscriptionId}`}>View the subscription</Link>
                </>
              )}
            </div>
          </div>
        )}

        {/* Not a reason: this item IS being created. It is what somebody would want to know about
            it afterwards — a customer written into a parameter nobody could confirm, or a date the
            report has no answer for — so it is rendered in full in the row, exactly like the
            refusals above, and never tucked behind a tooltip. */}
        {item.advisoryNote && (
          <div className="notice notice-warn" style={{ margin: '8px 0 0' }}>
            <strong>Worth knowing — this one is still created.</strong>
            <div>{item.advisoryNote}</div>
          </div>
        )}

        {/* Shown for every KT Insights item that carries one, including a blocked one — the fact
            that an export renders as an identity which sees all data is not less true because this
            particular item is not being created today. */}
        {item.renderIdentityWarning && (
          <div className="notice notice-warn" style={{ margin: '8px 0 0' }}>
            <strong>Render identity</strong>
            <div>{item.renderIdentityWarning}</div>
          </div>
        )}

        {/* PBIRS only. A KT Insights item's parameters are Power BI filters rather than report
            parameters, and its head office is already overwritten per customer — so there is
            nothing here it would edit. */}
        {item.producer === 'Pbirs' && packItem?.reportPath && (
          <CustomerParameters
            reportPath={packItem.reportPath}
            headOfficeParameterName={packItem.headOfficeParameterName ?? null}
            effective={item.effectiveParameters ?? []}
            missing={item.missingRequiredParameters ?? []}
            overrides={overrides}
            onOverride={onOverride}
            onClearOverride={onClearOverride}
          />
        )}
      </div>

      <div style={{
        fontSize: '.8rem',
        color: 'var(--kt-grey-500)',
        textAlign: 'right',
        flexShrink: 0,
        whiteSpace: 'nowrap'
      }}>
        {item.renderFormat && <>{item.renderFormat}<br /></>}
        {item.scheduleDescription}
      </div>
    </div>
  );
}

/**
 * This customer's parameters for one PBIRS item — the pack's values, editable, for this customer
 * only.
 *
 * <b>Two different needs, one control.</b> The first is preference: two customers can want the same
 * report on the same schedule sliced differently, last month by travel date for one and by invoice
 * date for another, and those are one pack rather than two. The second is that some parameters
 * cannot be answered anywhere else. A dynamic value list is produced by a dataset taking the
 * parameters above it as arguments, so anything below the bound head office — `CustomerId` on
 * /Sales/Scope 3, `DateFilterId` on Any Client Detail (Custom View) — has no list at all until a
 * customer is known. Here one is, so the report answers, and the admin picks from a real list.
 *
 * The editor is the same {@link ParametersEditor} the subscription pages use, which is what makes
 * the cascade, the searchable pickers and the identity warning work here without reimplementing any
 * of them.
 *
 * What is sent up is the DIFFERENCE from the pack, not the whole set — the pack stays the default
 * and this records only the exception to it, so a customer's placement can say what they were given
 * that the pack does not say.
 */
function CustomerParameters({
  reportPath,
  headOfficeParameterName,
  effective,
  missing,
  overrides,
  onOverride,
  onClearOverride
}: {
  reportPath: string;
  headOfficeParameterName: string | null;
  effective: ReportParameterValue[];
  missing: string[];
  overrides: ReportParameterValue[] | null;
  onOverride: (values: ReportParameterValue[]) => void;
  onClearOverride: () => void;
}) {
  const api = useApi();

  // Open on its own where a refusal points at it. An admin told "this report requires a value for
  // Customer Id" should not then have to find the place to give it one.
  const [open, setOpen] = useState(missing.length > 0);

  /**
   * What is on screen. Local, because the preview it was seeded from is re-fetched behind a debounce
   * — a field bound straight to the server's copy would snap back to its old value mid-keystroke.
   *
   * Seeded once per mount, and the row is remounted when the customer changes, so it can never
   * carry one customer's adjustments into the next.
   */
  const [edited, setEdited] = useState<ReportParameterValue[]>(effective);

  /** The pack's own values for this item, as the effective set stood before anything was adjusted. */
  const [baseline] = useState<ReportParameterValue[]>(effective);

  const { data: definitions, isFetching, error } = useQuery({
    queryKey: ['report-parameters', reportPath],
    queryFn: () => api.reportParameters(reportPath),
    enabled: open,
    retry: false
  });

  const bound = headOfficeParameterName?.trim().toLowerCase() ?? '';
  const changedCount = overrides?.length ?? 0;

  /**
   * The difference between what is on screen and what the pack says, as overrides.
   *
   * A parameter the pack pins and the admin has emptied is sent as an entry with NO values, which
   * the server reads as "clear it" rather than "pin an empty string". That distinction is the whole
   * of the difference between a report that computes its own window on every run and one frozen on
   * the day the pack was applied.
   */
  function diff(next: ReportParameterValue[]): ReportParameterValue[] {
    const result: ReportParameterValue[] = [];
    const same = (a: string[], b: string[]) => a.length === b.length && a.every((v, i) => v === b[i]);

    for (const value of next) {
      // The customer is not an adjustment: it is written at apply time from the head office, and
      // the server drops any override naming it. Excluded here too so it never reads as a change.
      if (bound && value.name.toLowerCase() === bound) continue;

      const before = baseline.find(b => b.name.toLowerCase() === value.name.toLowerCase());

      if (!before) {
        if (value.values.length > 0) result.push(value);
        continue;
      }

      if (!same(before.values, value.values)) result.push(value);
    }

    for (const before of baseline) {
      if (bound && before.name.toLowerCase() === bound) continue;
      if (before.values.length === 0) continue;

      const after = next.find(n => n.name.toLowerCase() === before.name.toLowerCase());
      if (!after || after.values.length === 0) result.push({ name: before.name, values: [], fromField: null });
    }

    return result;
  }

  function change(next: ReportParameterValue[]) {
    setEdited(next);
    const delta = diff(next);
    if (delta.length === 0) onClearOverride();
    else onOverride(delta);
  }

  return (
    <div style={{ marginTop: 8 }}>
      <button
        className="btn-ghost btn-sm"
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}>
        {open ? 'Hide' : missing.length > 0 ? 'Set the missing parameters' : 'Adjust for this customer'}
        {changedCount > 0 && !open && (
          <> · <strong>{changedCount} changed</strong></>
        )}
      </button>

      {open && (
        <div style={{
          marginTop: 8,
          padding: 12,
          border: '1px solid var(--kt-grey-200)',
          borderRadius: 'var(--radius-sm)'
        }}>
          <div className="field-hint" style={{ marginTop: 0 }}>
            These apply to <strong>this customer only</strong>. Everything left as it is stays the
            pack's, so a later change to the pack still reaches them.
            {headOfficeParameterName && (
              <> <code>{headOfficeParameterName}</code> is the customer and is filled in from the
              head office above, so it is shown here rather than offered.</>
            )}
            {changedCount > 0 && (
              <>
                {' '}
                <button className="btn-ghost btn-sm" type="button" onClick={() => change(baseline)}>
                  Reset to the pack
                </button>
              </>
            )}
          </div>

          {isFetching && !definitions ? (
            <div className="placeholder">Reading the report's parameters...</div>
          ) : error ? (
            <div className="error-banner">
              The report's parameters could not be read, so they cannot be adjusted here. This item
              would be created with the pack's values.
            </div>
          ) : (
            <ParametersEditor
              definitions={definitions ?? []}
              values={edited}
              onChange={change}
              reportPath={reportPath}
              // Shown, not offered. The server drops an override naming it, so a field that took an
              // edit here would silently discard it — worse than one that never accepted it.
              lockedNames={headOfficeParameterName ? [headOfficeParameterName] : undefined}
            />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * What the apply actually did, per item.
 *
 * Counted from `items` rather than from the result's own createdCount/failedCount, so a build that
 * does not send them cannot turn a partial apply into a silent success. A failure on one item never
 * stops the rest, so "created" and "failed" are both normal outcomes of one request and both are
 * named here.
 */
function ApplyResults({ result, onApplyAnother }: {
  result: ReportPackApplyResult;
  onApplyAnother: () => void;
}) {
  const created = result.items.filter(i => i.created);
  const failed = result.items.filter(i => !i.created);
  const withoutPickup = created.filter(i => i.pickupFailed);

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Applied “{result.packName}”</h1>
          <div className="sub">
            {result.headOfficeName} · {result.headOfficeId} ·{' '}
            {new Date(result.appliedUtc).toLocaleString()}
          </div>
        </div>
        <button className="btn-secondary" onClick={onApplyAnother}>Apply to another customer</button>
      </div>

      <div
        className={failed.length > 0 || withoutPickup.length > 0 ? 'notice notice-warn' : 'notice'}
        style={failed.length > 0 || withoutPickup.length > 0 ? undefined : { border: '1px solid var(--kt-grey-200)' }}
      >
        <strong>
          {created.length} of {result.items.length} item
          {result.items.length === 1 ? '' : 's'} created.
          {failed.length > 0 && ` ${failed.length} could not be created.`}
          {withoutPickup.length > 0 && ` ${withoutPickup.length} will not file to pickup.`}
        </strong>
        <div>
          A failure on one item never stops the rest — each is attempted independently, so you are
          not left working out which one aborted the batch and starting again. Re-applying the pack
          adds only what is missing.
        </div>
      </div>

      <div className="card">
        <table className="data-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Outcome</th>
              <th>Subscription</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {result.items.map(item => <ResultRow key={item.itemId} item={item} />)}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ResultRow({ item }: { item: ReportPackApplyItemResult }) {
  return (
    <tr>
      <td data-label="Item"><div className="report-name">{item.title}</div></td>
      <td data-label="Outcome">
        {!item.created ? (
          <span className="pill pill-failed">Not created</span>
        ) : item.pickupFailed ? (
          // Created and filing nowhere is not a success and not a failure — it is its own answer,
          // and the one most likely to go unnoticed, because the report does run.
          <span className="pill pill-pending">Created, no pickup</span>
        ) : (
          <span className="pill pill-success">Created</span>
        )}
      </td>
      <td data-label="Subscription">
        {item.subscriptionId
          ? <Link to={`/subscriptions/${item.subscriptionId}`}>View subscription</Link>
          : '—'}
      </td>
      <td data-label="Detail" style={{ color: 'var(--kt-grey-500)' }}>
        {item.error
          ? item.error
          : item.pickupFailed
            ? 'The subscription exists and will run, but its pickup route was not created — nothing '
              + 'will arrive in the customer’s folder until it is switched on from the subscription.'
            : '—'}
      </td>
    </tr>
  );
}
